/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useState } from "react";
import {
  DialogContent,
  Typography,
  Slide,
  Dialog,
  Button,
  DialogActions,
  DialogTitle,
  useTheme,
  FormControl,
  Chip,
  Box,
  CircularProgress,
} from "@mui/material";

import { commonStyles } from "theme/styles";
import { SubjectSearch } from "containers/rules/component/SubjectSearch";
import { get, post } from "lib/fetch";
import { IState, MCP_PREVIEW, RuleTargetType } from "store/types";
import { openSnackBar } from "actions/snackBar";
import { useDispatch, useSelector } from "react-redux";
import CheckIcon from "@mui/icons-material/Check";
import store from "store";
import { isEmpty } from "lodash";
import styled from "styled-components";
import { addSubjectDetails } from "actions/subjectDetails";

const Transition = React.forwardRef(function Transition(
  props: { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SpinnerWrapper = styled.div`
  padding-top: 20px;
  display: flex;
  justify-content: center;
  align-content: center;
`;

interface IProps {
  capsObject: any;
  onCancel: () => any;
}

export const MCPPreviewDialog = ({ onCancel, capsObject }: IProps) => {
  const theme = useTheme();
  const styles = commonStyles(theme);
  const subjectDetails = useSelector((s: IState) => s.subjectDetails);
  const dispatch = useDispatch();
  const { tokenValidated } = store.getState();

  const [isSubmitting, setSubmitting] = useState(0);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [components, setComponents] = useState<any[]>([]);
  const [courseSelected, setCourseSelected] = useState<string>();

  const getCourse = useCallback(
    async (sysId: string) => {
      try {
        const courseCapsObject = await get(
          `/v1/courserules?sysId=${sysId}&sessionId=${tokenValidated.sessionId}&attemptToExtractRules=${true}`,
        );
        return courseCapsObject;
      } catch (e) {
        console.log(e);
      }
      return undefined;
    },
    [tokenValidated.sessionId],
  );

  const onCourseSelected = useCallback(
    async (sysId: string) => {
      if (courseSelected !== sysId) {
        setCourseSelected(sysId);
        setComponents([]);
        try {
          setSubmitting(2);
          const components = await get(
            `/v1/search/component?sys_id=${sysId}&sessionId=${tokenValidated.sessionId}&parentCourseSysId=["${sysId}"]&searchYear=${capsObject.effectiveYear}`,
          );
          setComponents(components);
        } catch (e) {
          console.log(e);
        } finally {
          setSubmitting(0);
        }
      }
    },
    [capsObject.effectiveYear, courseSelected, tokenValidated.sessionId],
  );

  const getCourses = useCallback(
    async (sysId: string, type: string, effectiveYear: string, code: string, state: string) => {
      try {
        setSubmitting(1);
        const courses = await get(
          `/v1/courserules/coursesFor?sysId=${sysId}&type=${type}&year=${effectiveYear}&sessionId=${tokenValidated.sessionId}&code=${code}&state=${state}`,
        );
        if (courses.length === 0) {
          dispatch(
            openSnackBar({
              type: "error",
              message: `This ${type} is not referenced by any course`,
              autoHideDuration: 30000,
            }),
          );
        }
        setCourses(effectiveYear ? courses.filter((c: any) => `${c.year}` === effectiveYear) : courses);
      } catch (e) {
        console.log(e);
        setCourses([]);
      } finally {
        setSubmitting(0);
      }
    },
    [dispatch, tokenValidated.sessionId],
  );

  useEffect(() => {
    const getCourseInternal = async (sysId: string) => {
      try {
        setSubmitting(1);
        const courseCapsObject = await getCourse(sysId);
        if (courseCapsObject.courses) {
          setCourses([
            {
              name: courseCapsObject.courses.name,
              code: courseCapsObject.courses.code,
              type: courseCapsObject.courses.rules.type,
              recordId: courseCapsObject.courses.recordId,
              version: courseCapsObject.courses.version,
              year: courseCapsObject.courses.effectiveYear,
              rules: courseCapsObject.courses.rules,
              state: courseCapsObject.courses.state,
              sys_id: courseCapsObject.courses.versionId,
            },
          ]);
          await onCourseSelected(sysId);
        }
      } catch (e) {
        console.log(e);
        setCourses([]);
      } finally {
        setSubmitting(0);
      }
    };

    if (capsObject.rules.type.toLowerCase() === RuleTargetType.Course) {
      setCourses([{ ...capsObject, sys_id: capsObject.versionId }]);
      onCourseSelected(capsObject.versionId);
    } else if (capsObject.rules.type.toLowerCase() === RuleTargetType.Component) {
      setComponents([{ ...capsObject, sys_id: capsObject.versionId }]);
      getCourseInternal(capsObject.parentCourse.versionId);
    } else if (capsObject.rules.type.toLowerCase() === RuleTargetType.Subject) {
      setSubjects([capsObject.recordId]);
      dispatch(addSubjectDetails(capsObject));
      getCourses(
        capsObject.versionId,
        capsObject.rules.type,
        capsObject.effectiveYear,
        capsObject.code,
        capsObject.state,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capsObject, dispatch, tokenValidated]);

  const createWorkspaceAndSpawn = useCallback(
    async (course: any) => {
      try {
        setSubmitting(3);
        const workspace = {
          name: MCP_PREVIEW,
          owner: "staff",
          sessionId: tokenValidated.sessionId,
          items: [
            {
              name: course.name,
              code: course.code,
              type: course.rules.type,
              recordId: course.recordId,
              version: course.version,
              year: course.year,
              rules: course.rules,
              state: course.state,
              sysId: course.sys_id,
            },
          ],
        };
        components.forEach((c: any) => {
          workspace.items.push({
            name: c.name,
            code: c.code,
            type: "Component",
            recordId: c.recordId,
            version: c.version,
            year: course.year,
            rules: isEmpty(c.rules) ? "{}" : c.rules,
            state: c.state,
            sysId: c.versionId,
          });
        });
        subjects.forEach((s: any) => {
          workspace.items.push({
            name: subjectDetails[s].name,
            code: isEmpty(subjectDetails[s].code) ? subjectDetails[s].recordId : subjectDetails[s].code,
            type: "Subject",
            recordId: subjectDetails[s].recordId,
            version: subjectDetails[s].version,
            year: course.year,
            rules: isEmpty(subjectDetails[s].rules) ? "{}" : subjectDetails[s].rules,
            state: subjectDetails[s].state,
            sysId: subjectDetails[s].versionId!,
          });
        });
        const workspaceResponse = await post("/v1/workspaces", workspace);
        onCancel();
        window.open(
          `${workspaceResponse.baseUrl}/preview-plan/${course.recordId}/${course.effectiveYear}/${workspaceResponse.id}`,
          "_blank",
        );
      } catch (e) {
        console.log(e);
        dispatch(openSnackBar({ type: "error", message: "Failed to initiate MCP Preview", autoHideDuration: 30000 }));
      } finally {
        setSubmitting(0);
      }
    },
    [tokenValidated.sessionId, components, subjects, onCancel, subjectDetails, dispatch],
  );

  const onLoadAndPreview = useCallback(async () => {
    const course = courses.find((c) => c.sys_id === courseSelected);
    try {
      setSubmitting(3);
      const courseCapsObject = await getCourse(course.sys_id);
      createWorkspaceAndSpawn({ ...courseCapsObject.courses, sys_id: courseCapsObject.courses.versionId });
    } catch (e) {
      console.log(e);
      dispatch(
        openSnackBar({
          type: "error",
          message: `Failed to initiate MCP Preview`,
          autoHideDuration: 30000,
        }),
      );
      setSubmitting(0);
    }
  }, [courses, courseSelected, getCourse, createWorkspaceAndSpawn, dispatch]);

  const handleClose = (event: any, reason: any) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onCancel();
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      TransitionComponent={Transition}
      // For Accessibility - https://github.com/mui-org/material-ui/issues/18935#issuecomment-665835537
      TransitionProps={{ role: "presentation" } as any}
      aria-labelledby="mcppreview-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="mcppreview-dialog-title">
        {isSubmitting !== 3 && (
          <Typography color="primary" variant="h6">
            MCP Preview Loader : {capsObject.name} | {capsObject.code} | {capsObject.version}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {isSubmitting === 3 ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
            <Typography style={{ textAlign: "center" }} fontWeight="bold">
              Preparing workspace
            </Typography>
            <Typography style={{ textAlign: "center" }}>My Course Planner MCP Preview will start shortly</Typography>
            <br />
            <CircularProgress size={50} thickness={2} />
          </Box>
        ) : (
          <>
            <Typography fontWeight="bold">Courses to load</Typography>
            <Box
              sx={{
                borderRadius: 1,
                border: "1px solid rgba(0,0,0,0.24)",
                padding: "8px",
                gap: "8px",
                height: "120px",
                marginBottom: "24px",
                overflowY: "auto",
              }}
            >
              {isSubmitting === 1 ? (
                <SpinnerWrapper>
                  <CircularProgress size={50} thickness={2} />
                </SpinnerWrapper>
              ) : (
                <>
                  {courses.map((c: any) => (
                    <Chip
                      style={{ marginRight: 10, marginBottom: 10 }}
                      key={c.code}
                      clickable
                      onClick={() => onCourseSelected(c.sys_id)}
                      label={`${c.name} | ${c.code} | v${c.version}`}
                      icon={courseSelected === c.sys_id ? <CheckIcon fontSize="small" /> : undefined}
                    />
                  ))}
                </>
              )}
            </Box>
            <Typography fontWeight="bold">Components to load</Typography>
            <Box
              sx={{
                borderRadius: 1,
                border: "1px solid rgba(0,0,0,0.24)",
                padding: "8px",
                height: "120px",
                overflow: "auto",
                marginBottom: "24px",
              }}
            >
              {isSubmitting === 2 ? (
                <SpinnerWrapper>
                  <CircularProgress size={50} thickness={2} />
                </SpinnerWrapper>
              ) : (
                <>
                  {components.map((c: any) => (
                    <Chip
                      style={{ marginRight: 10, marginBottom: 10 }}
                      key={c.code}
                      label={`${c.type} : ${c.name} (${c.code})`}
                    />
                  ))}
                </>
              )}
            </Box>
            <Typography fontWeight="bold">Subjects to load</Typography>
            <FormControl fullWidth={true} variant="outlined">
              <SubjectSearch
                subjectsAndSets={subjects}
                onSubjectRecordIdsChanged={(v) => {
                  setSubjects(v);
                }}
                forPreview={true}
                readOnly={isEmpty(capsObject.rules) || capsObject.rules.type.toLowerCase() === RuleTargetType.Subject}
              />
            </FormControl>
          </>
        )}
      </DialogContent>
      {isSubmitting !== 3 && (
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={onCancel} sx={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onLoadAndPreview}
            sx={styles.saveButton}
            disabled={isEmpty(courseSelected)}
          >
            Load & Preview
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
